import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Alert, CircularProgress, ThemeProvider, Typography } from '@mui/material';
import INDIntakeForm from './INDIntakeForm';
import INDDocumentEditor from './INDDocumentEditor';
import INDPackageStatus from './INDPackageStatus';
import indService from '../services/ind.service';
import useIndProjectId from '../utils/useIndProjectId';
import indTheme from '../utils/indTheme';

const IND_TABS = [
  { label: 'IND Intake', value: 0 },
  { label: 'Documents', value: 1 },
  { label: 'Package Status', value: 2 },
];

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

const INDPage = () => {
  const { projectId, projectLoading } = useIndProjectId();
  const [tab, setTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  const [catalogueError, setCatalogueError] = useState(null);
  const [documentKey, setDocumentKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCatalogueLoading(true);
    indService.getCatalogue()
      .then((data) => {
        if (cancelled) return;
        const docs = [...(data?.documents || [])].sort(byOrder);
        setDocuments(docs);
        setDocumentKey((prev) => (prev && docs.some((d) => d.key === prev) ? prev : docs[0]?.key || null));
        setCatalogueError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('INDPage: failed to load IND catalogue', err);
        setCatalogueError(err.message || 'Failed to load the IND document catalogue');
      })
      .finally(() => {
        if (!cancelled) setCatalogueLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const selectedDocument = documents.find((d) => d.key === documentKey) || null;

  const renderDocuments = () => {
    if (catalogueLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (catalogueError) return <Alert severity="error">{catalogueError}</Alert>;
    if (documents.length === 0) return <Alert severity="info">The IND catalogue has no documents.</Alert>;
    return (
      <Box>
        <Tabs
          value={documentKey || false}
          onChange={(_, v) => setDocumentKey(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="IND documents"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}
        >
          {documents.map((doc) => (
            <Tab
              key={doc.key}
              value={doc.key}
              label={
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.shortTitle || doc.title}</Typography>
                  {doc.ctdLocation && <Typography variant="caption" color="text.secondary">CTD {doc.ctdLocation}</Typography>}
                </Box>
              }
            />
          ))}
        </Tabs>
        {selectedDocument && (
          <INDDocumentEditor
            key={selectedDocument.key}
            documentKey={selectedDocument.key}
            catalogueDocument={selectedDocument}
            projectId={projectId}
          />
        )}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={indTheme}>
      <Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="IND module tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 } }}
        >
          {IND_TABS.map((t) => (
            <Tab key={t.label} label={t.label} value={t.value} />
          ))}
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {!projectId && projectLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !projectId ? (
            <Alert severity="info">No study selected. Open a study to work on its IND package.</Alert>
          ) : (
            <>
              {tab === 0 && <INDIntakeForm projectId={projectId} />}
              {tab === 1 && renderDocuments()}
              {tab === 2 && <INDPackageStatus projectId={projectId} catalogueDocuments={documents} />}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default INDPage;
